import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { XCircle, Building, Calendar, ChevronRight, AlertCircle } from 'lucide-react';
import { format, parseISO, differenceInDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ExpiredSubscription } from '@/services/dashboard';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '@/lib/utils';

interface ExpiredSubscriptionsWidgetProps {
  subscriptions: ExpiredSubscription[];
  loading?: boolean;
}

const ExpiredSubscriptionsWidget: React.FC<ExpiredSubscriptionsWidgetProps> = ({
  subscriptions,
  loading = false
}) => {
  const navigate = useNavigate();

  const getCustomerName = (subscription: ExpiredSubscription) => {
    const customer = subscription.subscription_customers?.[0]?.customer;
    return customer?.name || 'Cliente não identificado';
  };

  const getPlanName = (subscription: ExpiredSubscription) => {
    const item = subscription.items?.[0];
    return item?.subscription_plan?.name || 'Plano não identificado';
  };

  const getTotalValue = (subscription: ExpiredSubscription) => {
    return subscription.items?.reduce((total, item) => {
      return total + (item.unit_price * item.quantity);
    }, 0) || 0;
  };

  const getDaysExpired = (endDate: string) => {
    return Math.abs(differenceInDays(new Date(), parseISO(endDate)));
  };

  const getStateBadge = (state: string) => {
    switch (state) {
      case 'expired':
        return <Badge variant="destructive">Expirada</Badge>;
      case 'active':
        return <Badge variant="secondary" className="bg-orange-100 text-orange-800">Ativa (Vencida)</Badge>;
      case 'trial':
        return <Badge variant="outline" className="border-orange-300 text-orange-700">Trial Vencido</Badge>;
      default:
        return <Badge variant="outline">{state}</Badge>;
    }
  };

  const getExpiredBadge = (daysExpired: number) => {
    if (daysExpired <= 7) {
      return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">Recente</Badge>;
    } else if (daysExpired <= 30) {
      return <Badge variant="outline" className="border-orange-300 text-orange-700">1 mês</Badge>;
    } else if (daysExpired <= 90) {
      return <Badge variant="outline" className="border-red-300 text-red-700">3 meses</Badge>;
    }
    return <Badge variant="destructive">Antiga</Badge>;
  };

  if (loading) {
    return (
      <Card className="border-0 shadow-sm h-full">
        <CardHeader className="pb-1 pt-3 px-4">
          <CardTitle className="flex items-center gap-2 text-sm font-extrabold">
            <XCircle className="h-4 w-4 text-red-500" />
            Assinaturas Expiradas
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4 pt-0 h-[calc(100%-55px)] flex flex-col">
          <div className="space-y-2 flex-1">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-sm h-full">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 pt-3 px-4">
        <CardTitle className="flex items-center gap-2 text-sm font-extrabold">
          <XCircle className="h-4 w-4 text-red-500" />
          Assinaturas Expiradas
        </CardTitle>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/subscriptions')}
          className="h-7 px-2"
        >
          Ver todas
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </CardHeader>
      <CardContent className="px-4 pb-4 pt-0 h-[calc(100%-55px)] flex flex-col">
        {subscriptions.length === 0 ? (
          <div className="text-center py-4 text-muted-foreground">
            <XCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>Nenhuma assinatura expirada</p>
          </div>
        ) : (
          <div className="space-y-2 flex-1 overflow-y-auto">
            {subscriptions.slice(0, 5).map((subscription) => {
              const daysExpired = getDaysExpired(subscription.end_date);
              return (
                <div
                  key={subscription.id}
                  className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 cursor-pointer transition-colors border-red-200 bg-red-50/30 min-h-[80px]"
                  onClick={() => navigate(`/subscriptions/${subscription.id}`)}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-extrabold truncate">
                        {getCustomerName(subscription)}
                      </p>
                      {getStateBadge(subscription.state)}
                      {getExpiredBadge(daysExpired)}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                      <span className="font-extrabold">{getPlanName(subscription)}</span>
                      <span>•</span>
                      <span className="font-extrabold">
                        {formatCurrency(getTotalValue(subscription))}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        Expirou há {daysExpired} dias ({format(parseISO(subscription.end_date), "dd/MM/yyyy", { locale: ptBR })})
                      </div>
                    </div>
                    <div className="flex items-center gap-1 mt-1 text-xs text-red-600">
                      <AlertCircle className="h-3 w-3" />
                      Ação necessária
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </div>
              );
            })}
          </div>
        )}
        
        {subscriptions.length > 0 && (
          <div className="mt-4 pt-3 border-t">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Total expiradas:</span>
                <span className="font-extrabold text-red-600">{subscriptions.length}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Valor perdido:</span>
                <span className="font-extrabold text-red-600">
                  {formatCurrency(subscriptions.reduce((total, sub) => total + getTotalValue(sub), 0))}
                </span>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ExpiredSubscriptionsWidget;
